import { useQuery } from '@tanstack/react-query';
import { get, fmt } from '../../lib/api';

export default function Dashboard() {
  const dash = useQuery({ queryKey: ['dash'], queryFn: () => get<any>('/api/v1/reports/dashboard') });
  const staff = useQuery({ queryKey: ['staffperf'], queryFn: () => get<any>('/api/v1/reports/staff-performance') });
  const stores = useQuery({
    queryKey: ['bystore'],
    queryFn: () => get<any>('/api/v1/reports/sales-by-store').catch(() => null),
  });
  const best = useQuery({
    queryKey: ['best'],
    queryFn: () => get<any>('/api/v1/reports/best-sellers?limit=10').catch(() => null),
  });

  const d = dash.data;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Dashboard</h1>
        <span className="text-xs text-gray-500">last 30 days · all figures VAT-inclusive</span>
      </div>

      {dash.isLoading && <div className="text-gray-500 text-sm">Loading…</div>}
      {dash.isError && <div className="text-red-300 text-sm">Could not load KPIs</div>}

      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Net revenue" value={fmt(d.netRevenueMinor)} sub="gross less refunds" big />
            <Kpi label="Transactions" value={String(d.sales.transactions)} sub={`${d.sales.units} units`} />
            <Kpi label="ATV" value={fmt(d.sales.atvMinor)} sub={`UPT ${d.sales.upt}`} />
            <Kpi
              label="Refund rate"
              value={`${d.refunds.ratePct}%`}
              sub={`${d.refunds.count} credit notes`}
              tone={d.refunds.ratePct > 25 ? 'warn' : undefined}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-4">
              <div className="label">VAT breakdown</div>
              <Line k="Gross (inc. VAT)" v={fmt(d.sales.grossMinor)} />
              <Line k="Taxable value" v={fmt(d.sales.netMinor)} />
              <Line k="VAT at 10%" v={fmt(d.sales.vatMinor)} />
              <Line k="Discounts given" v={fmt(d.sales.discountMinor)} />
              <div className="text-[11px] text-gray-500 mt-2">
                Taxable value + VAT always equals gross exactly — VAT is derived from the
                inclusive tag price, never added on top.
              </div>
            </div>

            {stores.data && (
              <div className="card p-4">
                <div className="label">By store</div>
                {stores.data.items.map((x: any) => (
                  <Line key={x.storeCode} k={`${x.storeName}`} v={fmt(Number(x.grossMinor))} sub={`${x.transactions} txn`} />
                ))}
                {!stores.data.items.length && <div className="text-xs text-gray-600">No sales yet</div>}
              </div>
            )}
          </div>

          {staff.data?.items?.length > 0 && (
            <div className="card">
              <div className="px-4 py-2 border-b border-gray-800 text-sm font-medium">
                Staff performance
                <span className="text-[11px] text-gray-500 font-normal ml-2">
                  commission is computed net of returns
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr>
                    <th className="th">Staff</th><th className="th">Txn</th><th className="th">Units</th>
                    <th className="th">Net sales</th><th className="th">ATV</th><th className="th">UPT</th>
                    <th className="th">Discount %</th><th className="th">Refund %</th>
                    <th className="th">Blind returns</th><th className="th">Commission</th>
                  </tr></thead>
                  <tbody>
                    {staff.data.items.map((x: any) => (
                      <tr key={x.userId}>
                        <td className="td">
                          <div>{x.name}</div>
                          <div className="text-[11px] text-gray-500">{x.empCode}</div>
                        </td>
                        <td className="td money">{x.transactions}</td>
                        <td className="td money">{x.units}</td>
                        <td className="td money font-medium">{fmt(x.netSalesMinor)}</td>
                        <td className="td money">{fmt(x.atvMinor)}</td>
                        <td className="td money">{x.upt}</td>
                        <td className={`td money ${x.discountRatePct > 15 ? 'text-amber-400' : ''}`}>{x.discountRatePct}%</td>
                        <td className={`td money ${x.refundRatePct > 30 ? 'text-red-400' : ''}`}>{x.refundRatePct}%</td>
                        <td className={`td money ${x.blindReturnCount > 0 ? 'text-red-400 font-semibold' : 'text-gray-600'}`}>
                          {x.blindReturnCount}
                        </td>
                        <td className="td money">{fmt(x.commissionMinor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-800">
                Discount % and refund % double as fraud signals. Blind returns — refunds with
                no original receipt — are the strongest single indicator and are highlighted.
              </div>
            </div>
          )}

          {best.data?.items?.length > 0 && (
            <div className="card">
              <div className="px-4 py-2 border-b border-gray-800 text-sm font-medium">Best sellers</div>
              <table className="w-full text-sm">
                <thead><tr>
                  <th className="th">Item</th><th className="th">Size</th>
                  <th className="th">Category</th><th className="th">Units</th><th className="th">Value</th>
                </tr></thead>
                <tbody>
                  {best.data.items.map((x: any, i: number) => (
                    <tr key={i}>
                      <td className="td">
                        <div className="truncate max-w-xs">{x.title}</div>
                        <div className="text-[11px] text-gray-500">{x.sku}</div>
                      </td>
                      <td className="td">{x.size ?? '—'}</td>
                      <td className="td text-gray-400">{x.category ?? '—'}</td>
                      <td className="td money">{x.units}</td>
                      <td className="td money">{fmt(Number(x.grossMinor))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, big, tone }: {
  label: string; value: string; sub?: string; big?: boolean; tone?: 'warn';
}) {
  return (
    <div className={`card p-4 ${tone === 'warn' ? 'border-amber-800' : ''}`}>
      <div className="label">{label}</div>
      <div className={`money font-bold ${big ? 'text-3xl' : 'text-2xl'} ${tone === 'warn' ? 'text-amber-300' : ''}`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Line({ k, v, sub }: { k: string; v: string; sub?: string }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-gray-400">{k}{sub && <span className="text-gray-600 text-xs ml-1">({sub})</span>}</span>
      <span className="money">{v}</span>
    </div>
  );
}
